import React, { useContext } from "react";
import clsx from "clsx";
import fp from "lodash/fp";
import { Form, Row, Col } from "react-bootstrap";
import { useFormContext, Controller, Control } from "react-hook-form";

import { ErrorMessage, RequiredMark, Skeleton } from "components";
import Select, { SelectProps } from "components/select";
import { customMultiSelectStyles } from "components/select/multiSelectComponents";
import styles from "components/select/selectAsync.module.scss";
import { selectStyles } from "components/select/selectStyles";
import { SaveInProgressWrapperContext } from "helpers";
import { ObjectGeneric } from "types";

export type HookSelectFormGroupProps = SelectProps & {
	name: string;
	cacheOptions?: boolean;
	debounceTimeout?: number;
	rules?: ObjectGeneric;
	control?: Control<any>; // Use this props only if component doesn't have <FormProvider /> wrapper
	components?: any;
	pleaseSelectText?: string;
	customStyles?: Object;
};

const HookSelectFormGroup: React.FC<HookSelectFormGroupProps> = ({
	name,
	label,
	disableSkeleton,
	options,
	blurLoading = false,
	preselect = {},
	placeholder = "Please Select",
	isDisabled = false,
	hideRequiredMark,
	showRequiredMark = false,
	hideLabel = false,
	colLabel = { sm: 6 },
	colSelect = { sm: 6 },
	containerClassName,
	classNameCol = "",
	getOptionLabel,
	getOptionValue,
	rules,
	onChange,
	brandingColor,
	pleaseSelectText,
	control,
	customStyles,
	...props
}) => {
	const {
		formState: { errors },
		control: contextControl,
	} = useFormContext();
	const { handleSave } = useContext(SaveInProgressWrapperContext);

	const showLoader =
		!disableSkeleton &&
		(blurLoading || (blurLoading === undefined && !options?.length));

	const errorMessage = fp.get(name, errors) && (
		<ErrorMessage field={name} errors={errors} />
	);

	const getOptions = () => {
		if (pleaseSelectText && options?.length) {
			return [
				{
					//Adding 'Select Text' as a option
					name: `${name}-default-option`,
					displayName: pleaseSelectText,
				},
				...options,
			];
		}
		return options;
	};

	const isSelectText = value => {
		return value?.name === `${name}-default-option`;
	};

	const refreshErrorMsg = renderProps => {
		renderProps?.onBlur();
		renderProps?.ref?.current?.select?.focus();
	};

	return (
		<Form.Group
			as={Row}
			className={clsx(
				styles.selectFormGroup,
				isDisabled && "disabled",
				containerClassName,
			)}
		>
			{!hideLabel && (
				<Form.Label column className={styles.selectLabel} {...colLabel}>
					{label}
					{showRequiredMark && <RequiredMark />}
				</Form.Label>
			)}
			<Col {...colSelect} className={clsx(classNameCol)}>
				{showLoader ? (
					<Skeleton height="34px" />
				) : (
					<Controller
						name={name}
						rules={rules}
						control={control || contextControl}
						render={({ field }) => (
							<Select
								{...props}
								{...field}
								options={getOptions()}
								hasError={!!errorMessage}
								placeholder={placeholder}
								isDisabled={isDisabled}
								styles={
									customStyles
										? customMultiSelectStyles
										: selectStyles
								}
								getOptionLabel={getOptionLabel}
								getOptionValue={getOptionValue}
								brandingColor={brandingColor}
								onChange={(value, { action }: any) => {
									if (isSelectText(value)) {
										field.onChange(null);
										onChange?.(null, { action });
									} else {
										field.onChange(value);
										onChange?.(value, { action });
									}
									refreshErrorMsg(field);
								}}
								onBlur={() => handleSave?.()}
							/>
						)}
						defaultValue={props.defaultValue}
					/>
				)}
				{!isDisabled && errorMessage}
			</Col>
		</Form.Group>
	);
};

export default HookSelectFormGroup;
